import React from 'react'
import classNames from 'classnames'
import ButtonBase, { Props as BaseProps } from '../ButtonBase/ButtonBase'
import config from './config'

export type Variant = keyof typeof config

export type Color<T extends Variant> = keyof (typeof config)[T]

interface CommonProps extends Omit<BaseProps, 'styled'> {
  size?: 'xs' | 'sm' | 'md' | 'lg'
  rounded?: boolean
  disabled?: boolean
  pill?: boolean
}

export interface VariantButton<T extends Variant> extends CommonProps {
  variant?: T
  color?: T extends Variant ? Color<T> : never
}

export type Props =
  | VariantButton<'primary'>
  | VariantButton<'secondary'>
  | VariantButton<'text'>
  | VariantButton<'link'>
  | VariantButton<'transparent'>

const Button: React.FC<Props> = ({
  variant = 'primary',
  color = 'oxide',
  size = 'md',
  rounded = true,
  disabled = false,
  pill = false,
  className,
  ...rest
}) => {
  // TODO: typescript warning about invalid variant/color combos
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const colorClassName = config[variant]?.[color]
  rounded = variant === 'link' || pill ? false : rounded

  return (
    <ButtonBase
      {...rest}
      styled
      className={classNames(
        'leading-tight focus:outline-none transition-all duration-150 disabled:pointer-events-none disabled:opacity-30',
        {
          'focus-visible:ring-2 focus-visible:ring-offset-2':
            variant !== 'link',
          'h-14 px-7': size === 'lg' && variant !== 'link',
          'h-12 px-6': size === 'md' && variant !== 'link',
          'h-10 px-5': size === 'sm' && variant !== 'link',
          'h-8 px-4': size === 'xs' && variant !== 'link',
          'rounded-[0.875rem]': rounded && size === 'lg',
          'rounded-xl': rounded && size === 'md',
          'rounded-[0.625rem]': rounded && size === 'sm',
          'rounded-lg': rounded && size === 'xs',
          'rounded-full': pill,
          'text-lg': size === 'lg',
          'text-sm': size === 'sm',
          'text-xs': size === 'xs',
          'font-semibold': variant === 'link',
        },
        [className, colorClassName]
      )}
      disabled={disabled}
    />
  )
}

export default Button
